'use client';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from './shadcn/popover';

/** A reading card opens only after the pointer rests, unlike the instant HelpTip/MarkStrip label. */
export const HOVER_CARD_OPEN_DELAY_MS = 600;
// Let the pointer cross the Popover's 8px anchor/content gap without closing the reading surface.
const CLOSE_DELAY_MS = 150;

/** An in-place, interactive reading card. Hover and focus span both the anchor and the scrollable
 *  body; opening never steals focus. The span preserves a containing row's click action. */
export function HoverCard({ children, content, side = 'bottom', label, className = '', contentClassName = '' }: {
  /** The anchor: the text the reader rests on. */
  children: ReactNode;
  /** The card's body. Long content is bounded and scrolls. */
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** The accessible name of the anchor, when the visible text does not name it. */
  label?: string;
  className?: string;
  contentClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorId = useId();
  const root = useRef<HTMLSpanElement>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
  }, []);
  const hide = useCallback(() => { cancel(); setOpen(false); }, [cancel]);
  useEffect(() => cancel, [cancel]);

  return (
    <span
      ref={root}
      className={`relative inline-flex min-w-0 ${className}`}
      // In-place content must paint above the neighbouring rows' lifted (`--z-lift`) anchors. This orders sibling
      // rows inside the rail, so it stays below the overlay scale (`--z-fab` and up in tokens.css).
      style={open ? { zIndex: 'var(--z-popover)' } : undefined}
      onMouseEnter={() => {
        cancel();
        if (!open) timer.current = setTimeout(() => { timer.current = null; setOpen(true); }, HOVER_CARD_OPEN_DELAY_MS);
      }}
      onMouseLeave={() => {
        cancel();
        if (!focused.current) timer.current = setTimeout(hide, CLOSE_DELAY_MS);
      }}
      onFocus={(event) => {
        focused.current = true;
        cancel();
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(true);
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        focused.current = false;
        // Popover's focus-outside event closes after the browser focuses the next control.
        // Unmounting during blur strands focus on a containing modal's surface.
      }}
    >
      <Popover open={open} onOpenChange={(next) => { if (!next) hide(); }}>
        <PopoverAnchor asChild>
          <span ref={anchor} id={anchorId} tabIndex={0} aria-label={label}
            className="min-w-0 truncate rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {children}
          </span>
        </PopoverAnchor>
        <PopoverContent
          role="region"
          aria-labelledby={anchorId}
          tabIndex={0}
          side={side}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => {
            if (event.target instanceof Node && root.current?.contains(event.target)) event.preventDefault();
          }}
          onEscapeKeyDown={() => {
            // Keep keyboard position when dismissing from the body, without reopening on internal focus.
            if (root.current?.contains(document.activeElement)) anchor.current?.focus();
          }}
          // The card sits inside a clickable rail row; reading/selecting its text is not row navigation.
          onClick={(event) => event.stopPropagation()}
          onKeyDownCapture={(event) => {
            // Popover's FocusScope loops Tab even when non-modal. Keep native document order here.
            if (event.key === 'Tab') event.stopPropagation();
          }}
          onKeyDown={(event) => {
            // Native buttons also activate from a focused descendant; reading is not row activation.
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) event.preventDefault();
          }}
          className={`max-h-[min(--spacing(64),var(--radix-popover-content-available-height))] max-w-[calc(100vw-var(--spacing)*8)] w-72 overflow-y-auto whitespace-pre-wrap break-words rounded-md p-3 text-meta normal-case text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring ${contentClassName}`}
        >
          {content}
        </PopoverContent>
      </Popover>
    </span>
  );
}
